import React, { useCallback, useEffect, useState } from "react";

import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Switch from "antd/es/switch";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { FormItem } from "@mapmycustomers/ui";

import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import isValidDate from "@app/util/isValidDate";

import FormValues from "../../types/FormValues";
import getCompletedValueBasedOnStartAt from "../../utils/getCompletedValueBasedOnStartAt";

import styles from "./ActivityCompleted.module.scss";

interface Props {
  creating?: boolean;
  disabled?: boolean;
  form: FormInstance<FormValues>;
  onChange?: () => void;
  tooltip?: string;
}

const messages = defineMessages({
  done: {
    id: "createActivityModal.footer.scheduleActivity.done",
    defaultMessage: "Done!",
    description: "Done!",
  },
  markDone: {
    id: "createActivityModal.footer.scheduleActivity.markDone",
    defaultMessage: "Mark as Done",
    description: "Mark as Done",
  },
  markNotDone: {
    id: "createActivityModal.footer.scheduleActivity.markNotDone",
    defaultMessage: "Mark as Not Done",
    description: "Mark as Not Done",
  },
  notDone: {
    id: "createActivityModal.footer.scheduleActivity.notDone",
    defaultMessage: "Not done yet",
    description: "Not done yet",
  },
});

const ActivityCompleted = ({ creating, disabled, form, onChange, tooltip }: Props) => {
  const intl = useIntl();
  const [firstChange, setFirstChange] = useState(true);

  const dateValue = form.getFieldValue(ActivityFieldName.START_AT);

  const handleCompletedToggle = useCallback(() => {
    setFirstChange(false);
    onChange?.();
  }, [onChange, setFirstChange]);

  useEffect(() => {
    if (creating && firstChange && isValidDate(dateValue)) {
      const date = new Date(dateValue);
      const completed = getCompletedValueBasedOnStartAt(date);
      if (completed !== undefined && completed !== form.getFieldValue("completed")) {
        form.setFieldsValue({
          completed,
        });
        onChange?.();
      }
    }
  }, [creating, dateValue, firstChange, form, onChange]);

  return (
    <div className={styles.container}>
      <FormItem
        noStyle
        shouldUpdate={(prevValues, curValues) => prevValues.completed !== curValues.completed}
      >
        {({ getFieldValue, setFieldsValue }) => {
          const isCompleted = getFieldValue(ActivityFieldName.COMPLETED);
          return (
            <>
              <div className={cn(styles.icon, { [styles.hidden]: !isCompleted })}>
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>

              <div>
                <Tooltip
                  overlayClassName={styles.tooltip}
                  placement="top"
                  title={
                    tooltip ??
                    (disabled
                      ? undefined
                      : isCompleted
                        ? intl.formatMessage(messages.markNotDone)
                        : intl.formatMessage(messages.markDone))
                  }
                >
                  <div
                    className={styles.markDone}
                    onClick={() => {
                      if (!disabled) {
                        setFieldsValue({ completed: !isCompleted });
                        handleCompletedToggle();
                      }
                    }}
                  >
                    <div className={styles.label}>
                      {isCompleted
                        ? intl.formatMessage(messages.done)
                        : intl.formatMessage(messages.notDone)}
                    </div>

                    <div className={styles.switch} onClick={stopEvents}>
                      <FormItem name={ActivityFieldName.COMPLETED} noStyle valuePropName="checked">
                        <Switch disabled={disabled} onChange={handleCompletedToggle} size="small" />
                      </FormItem>
                    </div>
                  </div>
                </Tooltip>
              </div>
            </>
          );
        }}
      </FormItem>
    </div>
  );
};

export default ActivityCompleted;
